import { Box, Divider, Grid, Typography } from '@mui/material'
import { DateRange } from '@mui/x-date-pickers-pro'
import { ReactElement, useMemo } from 'react'
import { SwitchContainer } from '../components'
import CheckBox from '../../../shared/UI/CheckBox'
import DateTimePicker from '../../../shared/UI/DateTimePickerV2'
import { IOSStyleSwitch } from '../../../shared/UI/IOSStyleSwitch/IOSStyleSwitch'
import InfoPopper from '../../../shared/UI/InfoPopper'
import { getShiftDateRange } from '../utils'

interface PartDayAbsenceFormProps {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  popperText: ReactElement | string
  shiftDescription: string
  dateRange: DateRange<Date>
  absentFrom: Date
  hasHadLunch: boolean
  toWorkBack: boolean
  handleDateChange: (selectedDate: Date | null, name: string, keyboardInputValue?: string) => void
  handleDateError: (error: boolean) => void
  handleCheckboxChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void
  isWorkBackDisabled?: boolean
  hasManAuthHistory?: boolean
  disabledPartDayAbsence: boolean
  disabledAbsentFrom: boolean
}

function PartDayAbsenceForm({
  isOpen,
  setIsOpen,
  popperText,
  shiftDescription,
  dateRange,
  absentFrom,
  hasHadLunch,
  toWorkBack,
  handleDateChange,
  handleDateError,
  handleCheckboxChange,
  isWorkBackDisabled = false,
  hasManAuthHistory = false,
  disabledPartDayAbsence,
  disabledAbsentFrom,
}: PartDayAbsenceFormProps): JSX.Element {
  const shiftDateRange = useMemo(() => getShiftDateRange(dateRange, shiftDescription), [dateRange, shiftDescription])
  return (
    <>
      <Grid container item xs={12} spacing={2}>
        <Grid item xs={6}>
          <SwitchContainer>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              id="part-day-container"
            >
              <Box display="flex" alignItems="center">
                <Typography variant="body1">Part Day Absence</Typography>
                <InfoPopper popperText={popperText} style={{ marginLeft: '4px' }} />
              </Box>

              <IOSStyleSwitch
                checked={isOpen}
                name="partDayAbsence"
                onChange={handleCheckboxChange}
                onClick={() => setIsOpen(!isOpen)}
                data-testid="MyActions-Absence-PartDayAbsence"
                disabled={disabledPartDayAbsence}
              />
            </Box>
          </SwitchContainer>
        </Grid>
        <Grid item xs={6}>
          <Box
            sx={{
              marginLeft: 'auto',
              '& label, & span': { whiteSpace: 'nowrap' },
              '& .MuiGrid-container': { marginRight: '108px' },
            }}
          >
            <CheckBox
              checked={toWorkBack}
              label="To work back"
              name="toWorkBack"
              onChange={handleCheckboxChange}
              dataTestId="MyActions-Absence-PartDayAbsence-ToWorkBack"
              disabled={isWorkBackDisabled || hasManAuthHistory}
            />
          </Box>
        </Grid>
      </Grid>
      {isOpen && (
        <Grid item xs={12} md={12}>
          <SwitchContainer>
            <Box display="flex" alignItems="center" width="100%" id="part-day-container">
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" width="100%">
                  <Box sx={{ flexGrow: 1, maxWidth: 'calc(100% - 258px)' }}>
                    <DateTimePicker
                      id="absence-from"
                      label="Absent from"
                      value={absentFrom}
                      minDateTime={shiftDateRange && shiftDateRange[0]}
                      maxDateTime={shiftDateRange && shiftDateRange[1]}
                      onChange={newValue => handleDateChange(newValue, 'absentFrom')}
                      onError={(error) => handleDateError(!!error)}
                      dataTestId="MyActions-Absence-PartDayAbsence-DateTime"
                      disabled={disabledAbsentFrom}
                    />
                  </Box>
                  <Box
                    sx={{
                      marginLeft: 'auto',
                      '& label, & span': { whiteSpace: 'nowrap' },
                      '& .MuiGrid-container': { marginRight: '100px' },
                    }}
                  >
                    <CheckBox
                      defaultChecked={hasHadLunch}
                      label="Has had lunch"
                      name="hasHadLunch"
                      onChange={handleCheckboxChange}
                      dataTestId="MyActions-Absence-PartDayAbsence-hasHadLunch"
                    />
                  </Box>
                </Box>
              </Grid>
            </Box>
          </SwitchContainer>
        </Grid>
      )}
      <Grid item xs={12} md={12}>
        <Divider sx={{ margin: 0 }} />
      </Grid>
    </>
  )
}

export default PartDayAbsenceForm
